"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import type { LocationMapType } from "@/lib/types/location";
import type { MapPoiType } from "@/lib/types/map-poi";
import { getStrapiMedia } from "@/lib/strapi/client";
import {
  DEFAULT_ZOOM,
  MAP_FIT_PADDING,
  POI_ZOOM,
  ZOOM_STEP_DELAY_MS,
} from "./constants";
import type {
  GoogleInfoWindow,
  GoogleMap,
  GoogleMapTypeStyle,
  LatLngBoundsLiteral,
  LatLngLiteral,
} from "./googleMaps";
import {
  createMainMarkerContent,
  createPoiInfoContent,
  createPoiMarkerContent,
  mountHtmlMarker,
  type MapHtmlMarker,
} from "./markers";
import {
  boundsCenter,
  boundsFromPois,
  poiCoordinates,
} from "./poiHelpers";

let mapsOptionsConfigured = false;

function ensureMapsOptions(apiKey: string) {
  if (mapsOptionsConfigured) return;
  setOptions({ key: apiKey });
  mapsOptionsConfigured = true;
}

function parseMapSkin(
  mapSkin: LocationMapType["mapSkin"],
): GoogleMapTypeStyle[] {
  if (!mapSkin) return [];
  return mapSkin as GoogleMapTypeStyle[];
}

function listenOnce(map: GoogleMap, eventName: string, handler: () => void) {
  const listener = map.addListener(eventName, () => {
    listener.remove();
    handler();
  });
}

function fitWhenIdle(
  map: GoogleMap,
  bounds: LatLngBoundsLiteral,
  onSettled?: (zoom: number) => void,
) {
  listenOnce(map, "idle", () => {
    map.fitBounds(bounds, MAP_FIT_PADDING);
    if (!onSettled) return;
    listenOnce(map, "idle", () => {
      const zoom = map.getZoom();
      if (zoom != null) onSettled(zoom);
    });
  });
}

type UseLocationMapArgs = {
  enabled: boolean;
  lat: number;
  lng: number;
  mapSkin: LocationMapType["mapSkin"];
  cloudMapId: string;
  mainMarkerIconUrl: string | null;
  mainMarkerLabel: string | null;
  enableMarker: boolean;
  filteredPois: MapPoiType[];
  /** When set, the map fits these bounds (POIs). */
  poiBounds?: LatLngBoundsLiteral | null;
};

export function useLocationMap({
  enabled,
  lat,
  lng,
  mapSkin,
  cloudMapId,
  mainMarkerIconUrl,
  mainMarkerLabel,
  enableMarker,
  filteredPois,
  poiBounds = null,
}: UseLocationMapArgs) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<GoogleMap | null>(null);
  const infoWindowRef = useRef<GoogleInfoWindow | null>(null);
  const infoWindowOpenRef = useRef(false);
  const mainMarkerRef = useRef<MapHtmlMarker | null>(null);
  const poiMarkersRef = useRef<MapHtmlMarker[]>([]);
  const defaultCenterRef = useRef<LatLngLiteral | null>(null);
  const defaultZoomRef = useRef(DEFAULT_ZOOM);
  const zoomAnimationRef = useRef<number | null>(null);
  const poiBoundsPropRef = useRef(poiBounds);
  const defaultViewBoundsRef = useRef<LatLngBoundsLiteral | null>(poiBounds);
  const focusPoiRef = useRef<(poi: MapPoiType) => void>(() => {});
  const resetMapViewRef = useRef<() => void>(() => {});
  const mainMarkerLabelRef = useRef(mainMarkerLabel);
  const mainMarkerIconUrlRef = useRef(mainMarkerIconUrl);

  const [mapReady, setMapReady] = useState(false);
  const [selectedPoi, setSelectedPoi] = useState<MapPoiType | null>(null);
  const [loadMap, setLoadMap] = useState(false);

  const cancelZoomAnimation = useCallback(() => {
    if (zoomAnimationRef.current === null) return;
    window.clearTimeout(zoomAnimationRef.current);
    zoomAnimationRef.current = null;
  }, []);

  const animateZoom = useCallback(
    (targetZoom: number) => {
      const map = mapRef.current;
      if (!map) return;

      cancelZoomAnimation();

      const currentZoom = map.getZoom() ?? defaultZoomRef.current;
      if (currentZoom === targetZoom) return;

      const direction = targetZoom > currentZoom ? 1 : -1;
      let nextZoom = currentZoom;

      const step = () => {
        if (!mapRef.current) {
          zoomAnimationRef.current = null;
          return;
        }

        if (
          (direction > 0 && nextZoom >= targetZoom) ||
          (direction < 0 && nextZoom <= targetZoom)
        ) {
          mapRef.current.setZoom(targetZoom);
          zoomAnimationRef.current = null;
          return;
        }

        nextZoom += direction;
        mapRef.current.setZoom(nextZoom);
        zoomAnimationRef.current = window.setTimeout(step, ZOOM_STEP_DELAY_MS);
      };

      zoomAnimationRef.current = window.setTimeout(step, 50);
    },
    [cancelZoomAnimation],
  );

  const clearPoiMarkers = useCallback(() => {
    poiMarkersRef.current.forEach((marker) => marker.clear());
    poiMarkersRef.current = [];
  }, []);

  const resetMapView = useCallback(() => {
    const map = mapRef.current;
    const defaultCenter = defaultCenterRef.current;
    if (!map || !defaultCenter) return;

    cancelZoomAnimation();
    infoWindowRef.current?.close();
    infoWindowOpenRef.current = false;
    setSelectedPoi(null);

    const fitBounds = defaultViewBoundsRef.current;
    if (fitBounds) {
      map.fitBounds(fitBounds, MAP_FIT_PADDING);
    } else {
      map.setCenter(defaultCenter);
      animateZoom(defaultZoomRef.current);
    }
  }, [animateZoom, cancelZoomAnimation]);

  const focusPoi = useCallback(
    (poi: MapPoiType) => {
      const map = mapRef.current;
      const position = poiCoordinates(poi);
      if (!map || !position) return;

      cancelZoomAnimation();

      setSelectedPoi(poi);
      map.setCenter(position);
      animateZoom(POI_ZOOM);

      const infoWindow = infoWindowRef.current;
      if (!infoWindow) return;

      infoWindow.setContent(createPoiInfoContent(poi));
      infoWindow.setPosition(position);

      if (!infoWindowOpenRef.current) {
        infoWindow.open({ map });
        infoWindowOpenRef.current = true;
      }
    },
    [animateZoom, cancelZoomAnimation],
  );

  useLayoutEffect(() => {
    focusPoiRef.current = focusPoi;
    resetMapViewRef.current = resetMapView;
    poiBoundsPropRef.current = poiBounds;
    mainMarkerLabelRef.current = mainMarkerLabel;
    mainMarkerIconUrlRef.current = mainMarkerIconUrl;
  }, [focusPoi, resetMapView, poiBounds, mainMarkerLabel, mainMarkerIconUrl]);

  useEffect(() => {
    if (!enabled) return;
    const el = containerRef.current;
    if (!el) return;

    const reveal = () => setLoadMap(true);
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight + 300) {
      reveal();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        reveal();
        observer.disconnect();
      },
      { rootMargin: "300px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !loadMap) return;

    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_EMBED_KEY;
    if (typeof apiKey !== "string" || !apiKey.trim()) {
      console.warn(
        "useLocationMap: NEXT_PUBLIC_GOOGLE_MAPS_EMBED_KEY is missing; map will not render.",
      );
      return;
    }

    ensureMapsOptions(apiKey);

    const bounds = poiBoundsPropRef.current;
    const seedCenter =
      !enableMarker && bounds ? boundsCenter(bounds) : { lat, lng };
    const mapTheme = parseMapSkin(mapSkin);
    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;

    async function initMap() {
      if (!containerRef.current || cancelled) return;

      const mapsLib = await importLibrary("maps");
      if (cancelled || !containerRef.current) return;

      const { Map: GoogleMapCtor, OverlayView, InfoWindow } = mapsLib;

      defaultCenterRef.current = seedCenter;
      defaultZoomRef.current = DEFAULT_ZOOM;

      const map = new GoogleMapCtor(containerRef.current, {
        center: seedCenter,
        zoom: DEFAULT_ZOOM,
        ...(cloudMapId
          ? { mapId: cloudMapId }
          : mapTheme.length > 0
            ? { styles: mapTheme }
            : {}),
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });

      mapRef.current = map;

      const infoWindow = new InfoWindow({ disableAutoPan: true });
      infoWindow.addListener("closeclick", () => {
        infoWindowOpenRef.current = false;
        resetMapViewRef.current();
      });
      infoWindowRef.current = infoWindow;

      if (enableMarker) {
        const markerLib = cloudMapId ? await importLibrary("marker") : null;
        if (cancelled) return;

        mainMarkerRef.current = mountHtmlMarker(
          map,
          OverlayView,
          markerLib?.AdvancedMarkerElement,
          {
            position: seedCenter,
            content: createMainMarkerContent(
              mainMarkerIconUrlRef.current,
              mainMarkerLabelRef.current ?? "",
            ),
            title: mainMarkerLabelRef.current ?? "",
            anchorBottom: false,
            zIndex: 2,
          },
        );
      }

      const applyMarkerCenter = () => {
        const current = mapRef.current;
        if (!current || !enableMarker) return;
        current.setCenter(seedCenter);
        current.setZoom(DEFAULT_ZOOM);
      };

      const container = containerRef.current;
      if (container && typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          const current = mapRef.current;
          if (!current) return;
          const mapsEvent = (
            mapsLib as typeof mapsLib & {
              event?: { trigger: (instance: object, name: string) => void };
            }
          ).event;
          mapsEvent?.trigger(current, "resize");
          // Layout A map pane sizes after mount — re-center on the property marker.
          if (enableMarker) {
            applyMarkerCenter();
          } else {
            const mapCenter = current.getCenter();
            if (mapCenter) current.setCenter(mapCenter);
          }
        });
        resizeObserver.observe(container);
      }

      if (cancelled) {
        resizeObserver?.disconnect();
        return;
      }

      // Default: zoom/center on property marker. Do not fitBounds to POIs (that
      // shifts the marker to the right edge of the map pane).
      if (enableMarker) {
        defaultViewBoundsRef.current = null;
        defaultCenterRef.current = seedCenter;
        defaultZoomRef.current = DEFAULT_ZOOM;
        applyMarkerCenter();
        // Re-apply after the first idle so layout/tiles cannot leave a tight zoom.
        const mapsEvent = (
          mapsLib as typeof mapsLib & {
            event?: {
              addListenerOnce: (
                instance: object,
                name: string,
                handler: () => void,
              ) => void;
            };
          }
        ).event;
        mapsEvent?.addListenerOnce(map, "idle", () => {
          if (cancelled || !mapRef.current) return;
          applyMarkerCenter();
        });
      } else if (bounds) {
        defaultViewBoundsRef.current = bounds;
        fitWhenIdle(map, bounds, (zoom) => {
          if (!mapRef.current) return;
          defaultZoomRef.current = zoom;
        });
      }

      setMapReady(true);
    }

    void initMap().catch(console.error);

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      setMapReady(false);
      cancelZoomAnimation();
      clearPoiMarkers();

      infoWindowRef.current?.close();
      infoWindowRef.current = null;
      infoWindowOpenRef.current = false;

      mainMarkerRef.current?.clear();
      mainMarkerRef.current = null;
      mapRef.current = null;
      defaultCenterRef.current = null;
    };
  }, [
    enabled,
    loadMap,
    lat,
    lng,
    mapSkin,
    cloudMapId,
    enableMarker,
    cancelZoomAnimation,
    clearPoiMarkers,
  ]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    const map = mapRef.current;
    clearPoiMarkers();
    if (filteredPois.length === 0) return;

    let cancelled = false;

    async function renderPoiMarkers() {
      const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_EMBED_KEY;
      if (!apiKey) return;

      ensureMapsOptions(apiKey);

      const mapsLib = await importLibrary("maps");
      const { OverlayView } = mapsLib;
      const markerLib = cloudMapId ? await importLibrary("marker") : null;

      if (cancelled || !mapRef.current) return;

      for (const poi of filteredPois) {
        const poiPos = poiCoordinates(poi);
        if (!poiPos) continue;

        const markerUrl =
          getStrapiMedia(poi.category?.poiMarker?.url ?? null) || null;
        const content = createPoiMarkerContent(markerUrl, poi.title ?? "");

        poiMarkersRef.current.push(
          mountHtmlMarker(map, OverlayView, markerLib?.AdvancedMarkerElement, {
            position: poiPos,
            content,
            title: poi.title ?? "",
            onClick: () => focusPoiRef.current(poi),
          }),
        );
      }

      if (!enableMarker) {
        const nextBounds = boundsFromPois(filteredPois);
        if (nextBounds) {
          defaultCenterRef.current = boundsCenter(nextBounds);
          defaultViewBoundsRef.current = nextBounds;
        }
      }
    }

    void renderPoiMarkers().catch(console.error);

    return () => {
      cancelled = true;
      clearPoiMarkers();
    };
  }, [filteredPois, mapReady, cloudMapId, enableMarker, clearPoiMarkers]);

  useEffect(() => {
    if (!selectedPoi) return;

    const stillVisible = filteredPois.some((poi) => poi.id === selectedPoi.id);
    if (!stillVisible) {
      resetMapView();
    }
  }, [filteredPois, selectedPoi, resetMapView]);

  return {
    containerRef,
    mapReady,
    selectedPoi,
    focusPoi,
    resetMapView,
  };
}
